import { useEffect, useMemo, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowSvg from '~/@ui/_sharedSvgs/ui/Arrow.svg?react'
import { Controller, Form, useForm } from 'react-hook-form'
import { OneTuneInvestmentSlider } from '~/@ui/slider/OneTuneInvestmentSlider/OneTuneInvestmentSlider'
import { InvestmenSlider } from '~/@ui/slider/InvestmentSlider/InvestmentSlider'
import { IInvestmentProduct } from '~/@api/generated-api-types/global.types'
import { orderScheme, OrderScheme } from './_scheme/order.scheme'
import { zodResolver } from '@hookform/resolvers/zod'
import { StyledSwitch } from '~/@ui/switch/StyledSwitch'
import { getApi } from '~/@utils/api/getApi'
import { useAlert } from '~/@ui/alert/useAlert'
import { GraphSummaryChildren } from './GraphSummaryChildren'
import { GraphSummaryPension } from './GraphSummaryPension'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { RecapModal } from './RecapModal'
import { InfoBubble } from '~/@ui/info-bubble/InfoBubble'
import { GraphSummary } from './GraphSummary'
import { calculateInvestmentProjections } from '~/@ui/chart/_utils/investmentCalculator'
import Chart from '~/@ui/chart/Chart'
import { OpportunityAvatar } from '../opportunities/OpportunityAvatar'
import { useCurrentUserData } from '~/@biz_account/useCurrentUserData/useCurrentUserData'

export const OpportunityPage = () => {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [opportunity, setOpportunity] = useState<IInvestmentProduct | null>(
    null
  )

  const isSavingsChildren = slug === 'buducnost-mojich-deti'
  const isSavingsRetirement = slug === 'zabezpecenie-financii-na-dochodok'
  // const isSavingsBeatInflation = slug === 'sporte-a-porazte-inflaciu'

  const defultFormValuesChildren = {
    oneTimeInvestmentAmount: 2000,
    monthlyInvestmentAmount: 80,
  }
  const defultFormValuesPension = {
    oneTimeInvestmentAmount: 5000,
    monthlyInvestmentAmount: 150,
  }
  const defultFormValuesSavings = {
    oneTimeInvestmentAmount: 2000,
    monthlyInvestmentAmount: 80,
  }

  const { watch, control, getValues, setValue } = useForm<OrderScheme>({
    resolver: zodResolver(orderScheme),
    defaultValues: {
      hasOneTimeInvestment: true,
      hasMonthlyInvestment: true,
      investmentProductId: opportunity?.id,
      riskAcknowledged: true,
      durationInMonths: 180,
      ...(isSavingsChildren
        ? defultFormValuesChildren
        : isSavingsRetirement
          ? defultFormValuesPension
          : defultFormValuesSavings),
    },
  })

  const { user } = useCurrentUserData()

  const hasOneTimeInvestment = watch('hasOneTimeInvestment')

  const navigateToOpportunities = () => navigate('/investor/opportunities')

  const availableFunds = user?._investorProfile?._investorWallet?.funds ?? 0

  const oneTimeInvestmentAmount = watch('hasOneTimeInvestment')
    ? watch('oneTimeInvestmentAmount')
    : 0
  const monthlyInvestmentAmount = watch('monthlyInvestmentAmount')
  const initialDeposit = oneTimeInvestmentAmount + monthlyInvestmentAmount
  const interestPerAnnum = opportunity?.interestPerAnnum ?? 0

  const getOpportunity = async () => {
    try {
      const { data: response } =
        await getApi().investmentProductsAppApiControllerGetInvestmentProduct(
          slug
        )

      if ('success' in response) {
        setOpportunity(response.investmentProduct)
        return
      }

      throw new Error('Nastala chyba pri načítavaní investičných príležitostí.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const createInvestment = async () => {
    try {
      const { data: response } =
        await getApi().investmentOrdersAppApiControllerCreateNonCommitalInvestmentOrder(
          getValues()
        )

      if ('success' in response) {
        showAlert('success', 'Investícia prebehla úspešne.')
        navigate(`/investor/portfolio/investments`) // TODO ma presmerovat na detail, ale type mi nedava investmentId zatial
        return
      }

      throw new Error('Nasatala neznáma chyba pri vytváraní objednávky.')
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
      throw err // Re-throw error so RecapModal can catch it
    }
  }

  const investmentYears = isSavingsRetirement ? 41 : 33

  const series = useMemo(
    () =>
      calculateInvestmentProjections({
        monthlyContribution: monthlyInvestmentAmount,
        investmentYears,
        initialDeposit,
        rates: {
          earlyWithdraw: 0.0359,
          bonaship: 0.0718,
          cash: 0,
          bankSavings: 0.015,
          inflation: 0.04,
        },
      }),
    [monthlyInvestmentAmount, investmentYears, initialDeposit]
  )

  useEffect(() => {
    setValue('investmentProductId', opportunity?.id || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunity])

  useEffect(() => {
    getOpportunity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack className="w-screen h-screen overflow-auto">
      <Stack className="flex-row min-h-[70px] items-center gap-3 px-6">
        <ArrowSvg
          className="cursor-pointer"
          onClick={navigateToOpportunities}
        />
        <Stack className="">
          <Typography className="text-base font-bold text-zBlack200">
            Objednávka investície
          </Typography>
          <Typography className="text-xs/[14px] font-medium text-zGrey400">
            {opportunity?.name || '...'}
          </Typography>
        </Stack>
      </Stack>

      <Form control={control} className="w-full max-w-[768px] self-center">
        <Stack className="pb-28 bg-zBlack200 rounded-t-4d">
          <Stack className="px-6 ">
            <Stack className="flex-row items-center gap-2 pt-6 pb-4">
              <OpportunityAvatar
                slug={slug}
                mode="dark"
                proportionsClassNames="w-16 h-16"
              />
              <Stack>
                <Typography className="text-base font-bold text-zWhite200">
                  {opportunity?.name || '...'}
                </Typography>
                <Typography
                  className="text-xs font-bold text-zGrey450 underline underline-offset-2 cursor-pointer"
                  onClick={navigateToOpportunities}>
                  Zmeniť produkt
                </Typography>
              </Stack>
            </Stack>

            <Typography className="text-3d font-medium text-zGrey400 pt-4 pb-3">
              {opportunity?.description}
            </Typography>

            <Typography className="text-xl font-bold text-zWhite200 mt-4">
              Chcem investovať
            </Typography>

            <Stack className="px-4 pt-[9px] pb-1 mt-4 bg-zBlack300 rounded-4d">
              <Stack className="flex-row items-center pb-1d">
                <InfoBubble
                  text="Vložte jednorazovú sumu, ktorá pozitívne ovplyvní váš budúci výnos."
                  className="mr-1"
                />
                <Typography className="text-base font-semibold text-zWhite200">
                  Jednorazová investícia
                </Typography>
                <Controller
                  name="hasOneTimeInvestment"
                  control={control}
                  render={({ field }) => (
                    <StyledSwitch
                      {...field}
                      checked={field.value}
                      className="ml-auto"
                      name="hasOneTimeInvestment"
                    />
                  )}
                />
              </Stack>
              {hasOneTimeInvestment && (
                <Stack className="">
                  <OneTuneInvestmentSlider
                    suffix={'€'}
                    name={'oneTimeInvestmentAmount'}
                    control={control}
                    min={300}
                    max={50000}
                    step={50}
                    className="mt-[19px]"
                  />
                </Stack>
              )}
            </Stack>

            <InvestmenSlider
              suffix={'€'}
              name={'monthlyInvestmentAmount'}
              control={control}
              min={25}
              max={2000}
              step={10}
              className="mt-3"
            />

            <Stack className="gap-3 mt-3">
              <Stack className={`p-4 bg-zBlack300 rounded-4d`}>
                <Stack className="flex-row gap-1 mb-4">
                  <InfoBubble text="Graf ukazuje, ako sa vaša investícia vyvíja v čase." />
                  <Typography className="text-base font-bold text-zWhite200">
                    Graf výnosov
                  </Typography>
                </Stack>

                <Stack className="relative border border-zGrey300 rounded-t-2d">
                  <Chart
                    series={series}
                    months={investmentYears * 12}
                    slug={slug}
                  />

                  <GraphSummary
                    className="z-10"
                    earlyWithdraw={0}
                    bonaship={Math.round(series.bonashipValue[360] || 0)}
                    inflation={0}
                    bankSavings={Math.round(series.bankSavingsValue[360] || 0)}
                    cash={Math.round(series.cashValue[360] || 0)}
                    months={360}
                    intPerAnn={series.rates.bonaship}
                    slug={slug}
                  />
                </Stack>

                <Stack className="bottom-[0px] w-full flex-row justify-between mt-2.5">
                  <Typography className="text-xs font-medium text-zGrey400">
                    0 rokov
                  </Typography>
                  <Typography className="text-xs font-medium text-zGrey400">
                    {isSavingsRetirement ? 20 : 15} rokov
                  </Typography>
                  <Typography className="text-xs font-medium text-zGrey400">
                    {isSavingsRetirement ? 40 : 30} rokov
                  </Typography>
                </Stack>
              </Stack>

              {isSavingsChildren && (
                <GraphSummaryChildren
                  years18={Math.round(
                    series.bonashipValue[216] || 0
                  ).toLocaleString('sk-SK')}
                  years25={Math.round(
                    series.bonashipValue[300] || 0
                  ).toLocaleString('sk-SK')}
                  years30={Math.round(
                    series.bonashipValue[360] || 0
                  ).toLocaleString('sk-SK')}
                  interestPerAnnum={interestPerAnnum}
                />
              )}

              {isSavingsRetirement && <GraphSummaryPension series={series} />}
            </Stack>
          </Stack>
          <Button
            variant="contained"
            className="h-14 mx-6 mt-10 bg-zGrey200 text-3d font-medium text-zBlack200"
            onClick={() => setIsModalOpen(true)}>
            Vyzerá to dobre, pokračovať
          </Button>
        </Stack>
      </Form>

      <RecapModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        opportunity={opportunity}
        availableFunds={availableFunds}
        orderDetails={getValues()}
        createInvestment={createInvestment}
      />
    </Stack>
  )
}
